import FollowControl from 'components/FollowControl'
import {
  usePostApiV1UsersFollowByUsernameAcceptMutation,
  usePostApiV1UsersFollowByUsernameRejectMutation,
} from '@wellesley/openapi'
import { FC } from 'react'
import { Button, cn } from '@wellesley/ui'
import { NotificationItem } from '../../../types'
import { useDispatch } from 'react-redux'
import { notificationsSlice } from '../../../slice'
import { FollowMutationCommonProps, useRelationship } from '@wellesley/user'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import checkNotificationType from '../../../helpers/checkNotificationType'

type FollowingControlsProps = {
  isPopup: boolean
  notification: NotificationItem
}

const FollowingControls: FC<FollowingControlsProps> = ({
  isPopup,
  notification,
}) => {
  const dispatch = useDispatch()

  const [acceptFollower] = usePostApiV1UsersFollowByUsernameAcceptMutation()
  const [rejectFollower] = usePostApiV1UsersFollowByUsernameRejectMutation()

  const author = notification.fromUser

  const { relationship, isLoading } = useRelationship({
    userId: author?.id,
  })

  const { isFollow, isFollowRequest } = checkNotificationType(notification)

  const canFollowBack = !!(author && isFollow)
  const canControlFollowRequest = !!(author && isFollowRequest)

  const username = author ? getUserNameWithDomain(author) : ''

  const onFollowAccept = () => {
    if (!author) return

    void acceptFollower({
      username,
      user: author,
    } as FollowMutationCommonProps)

    dispatch(
      notificationsSlice.actions.updateNotification({
        ...notification,
        followResponse: 'Accepted',
      }),
    )
  }

  const onFollowReject = () => {
    if (!author) return

    void rejectFollower({
      username,
      user: author,
    } as FollowMutationCommonProps)

    dispatch(
      notificationsSlice.actions.updateNotification({
        ...notification,
        followResponse: 'Rejected',
      }),
    )
  }

  const followResponse =
    notification.followResponse || notification.meta?.actionsTaken?.[0].text

  return (
    <>
      {canFollowBack && author && (
        <FollowControl
          size="s"
          user={author}
          relationship={relationship}
          isRelationshipPending={isLoading}
        />
      )}
      {followResponse ? (
        <div
          className={cn(
            'min-h-xl inline-flex min-w-[120px] items-center text-xs font-medium text-gray-500',
            !isPopup && 'justify-end',
          )}
        >
          {followResponse.toUpperCase()}
        </div>
      ) : canControlFollowRequest ? (
        <div className="flex items-center gap-2" data-testid="followControls">
          <Button
            size="s"
            variant="secondary"
            className="min-w-[100px] px-3"
            onClick={onFollowReject}
          >
            Reject
          </Button>
          <Button
            size="s"
            variant="primary"
            className={cn('min-w-[100px]', isPopup && 'order-first')}
            onClick={onFollowAccept}
          >
            Accept
          </Button>
        </div>
      ) : null}
    </>
  )
}

export default FollowingControls
